import {
  type HighlightsController,
  type HighlightsRenderer,
} from '@databases/highlights';
import { HIGHLIGHT_ACTIVE_CLASS } from '@databases/highlights/constants';

import { createEffect } from '../utils/create-effect';

import { syncRangeToHighlightsLayer } from './utils/sync-range-to-highlights-layer';

/** Must match `COLUMN_RESIZE_EVENT` in `@databases/table-resizer`. */
const COLUMN_RESIZE_EVENT_NAME = 'database-column-resize';

/**
 * Creates the main effect that draws / renders selection highlights
 * (the blue backgrounds).
 */
export const createHighlightsEffect = createEffect((controller: HighlightsController) => {
  // ---------------------------------------------------------------------------
  // DOM
  // ---------------------------------------------------------------------------
  let scrollableContainer: HTMLElement | null = null;

  // ---------------------------------------------------------------------------
  // Renderers
  // ---------------------------------------------------------------------------
  let highlightsRenderer: HighlightsRenderer | null = null;

  function ensureDependentElementsAndRenderer() {
    if (!scrollableContainer) {
      scrollableContainer = controller.getScrollContainer();
    }

    if (!highlightsRenderer && scrollableContainer) {
      highlightsRenderer = controller.createLayer();
    }

    return !!(highlightsRenderer && scrollableContainer);
  }

  // ---------------------------------------------------------------------------
  // Column-resize listener: redraws *this* layer when columns change width
  // so selection highlights stay aligned with their cells.
  // ---------------------------------------------------------------------------
  function handleColumnResize() {
    controller.clearVirtualGeometryCache();
    highlightsRenderer?.redrawAll();
  }

  return {
    deltaTypes: [
      'selectionModified',
      'selectionReplaced',
      'selectionPreview',
      'cellEditRequested',
      'cellEditStopped',
    ],

    setup() {
      document.addEventListener(COLUMN_RESIZE_EVENT_NAME, handleColumnResize);
    },

    dispose() {
      document.removeEventListener(COLUMN_RESIZE_EVENT_NAME, handleColumnResize);

      if (highlightsRenderer) {
        highlightsRenderer.dispose();
        controller.removeLayer(highlightsRenderer);
        highlightsRenderer = null;
      }

      if (scrollableContainer) {
        scrollableContainer.classList.remove(HIGHLIGHT_ACTIVE_CLASS);
      }

      scrollableContainer = null;
    },

    onDelta(delta, state) {
      if (
        !ensureDependentElementsAndRenderer() ||
        !highlightsRenderer ||
        !scrollableContainer
      ) {
        return;
      }

      if (state.mode === 'idle' || state.selections.length === 0) {
        scrollableContainer.classList.remove(HIGHLIGHT_ACTIVE_CLASS);

        controller.clearVirtualGeometryCache();
      } else {
        scrollableContainer.classList.add(HIGHLIGHT_ACTIVE_CLASS);
      }

      if (delta.kind === 'selectionPreview') {
        syncRangeToHighlightsLayer(highlightsRenderer, state.mode, delta.activeRange);

        return;
      }

      if (delta.kind === 'cellEditRequested') {
        highlightsRenderer.hideAll();

        return;
      }

      if (delta.kind === 'cellEditStopped') {
        highlightsRenderer.showAll();

        return;
      }

      // Handle Selections (The Blue Backgrounds)
      if (delta.kind === 'selectionReplaced') {
        highlightsRenderer.clear();

        for (const range of delta.selections) {
          syncRangeToHighlightsLayer(highlightsRenderer, state.mode, range);
        }

        return;
      }

      if (delta.kind === 'selectionModified') {
        if (delta.rangeIdsToRemove) {
          for (const id of delta.rangeIdsToRemove) {
            highlightsRenderer.remove(id);
          }
        }

        if (delta.added) {
          for (const range of delta.added) {
            syncRangeToHighlightsLayer(highlightsRenderer, state.mode, range);
          }
        }

        if (delta.updated) {
          for (const range of delta.updated) {
            syncRangeToHighlightsLayer(highlightsRenderer, state.mode, range);
          }
        }
      }
    },
  };
});
